import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { uploadChallenge, deleteChunks } from '../api/challenges';
import {
  Upload,
  Trash2,
  AlertCircle,
  CheckCircle,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  Info,
  FileCode,
  Layers,
  Image
} from 'lucide-react';
import { fetchDisplayChallengesPage, fetchDisplayChallengeById } from './firestoreFunctions';
import { WorkgroupSelect } from './WorkgroupSelect';
import { Workgroup } from '../types';
import {
  MasterDetailLayout,
  MasterListItem,
  DetailPanelHeader,
  EmptyState,
} from './MasterDetailLayout';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import DetailSection from "./detail/DetailSection";
import ImageUploadDropzone from './ImageUploadDropzone';
import ChallengeDetailsPanel from './ChallengeDetailsPanel';

interface Challenge {
  id: string;
  integration_id?: string | null;
  workgroup_id?: string | null;
  created_at?: string | null;
  status?: string | null;
  chunks?: string[];
  image_base64?: string;
  guidance?: string;
  max_turns?: number;
  system_prompt?: string;
  user_prompt_context?: string;
  activity_history?: string[];
}

const DETAIL_BACKGROUND = "#e8effa";

const ChallengesNew: React.FC = () => {
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [guidanceText, setGuidanceText] = useState('');
  const [maxTurns, setMaxTurns] = useState('');
  const [workgroups, setWorkgroups] = useState<Workgroup[]>([]);
  const [selectedWorkgroup, setSelectedWorkgroup] = useState('');
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [selectedChallengeLoading, setSelectedChallengeLoading] = useState(false);
  const [selectedChallengeError, setSelectedChallengeError] = useState<string | null>(null);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const latestSelectedChallengeIdRef = useRef<string | null>(null);

  // Pagination state
  const pageSize = 10;
  const [pages, setPages] = useState<Challenge[][]>([]);
  const [pageCursors, setPageCursors] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const handleWorkgroupsLoaded = useCallback(
    (loadedWorkgroups: Workgroup[]) => {
      setWorkgroups(loadedWorkgroups);
      if (!loadedWorkgroups.length) {
        setSelectedWorkgroup('');
        return;
      }

      const currentExists = loadedWorkgroups.some(
        (wg) => wg.id === selectedWorkgroup
      );

      if (!selectedWorkgroup || !currentExists) {
        setSelectedWorkgroup(loadedWorkgroups[0].id);
      }
    },
    [selectedWorkgroup]
  );

  // Update integrations when workgroup changes
  useEffect(() => {
    if (selectedWorkgroup && workgroups.length > 0) {
      const currentWorkgroup = workgroups.find((wg) => wg.id === selectedWorkgroup);
      if (currentWorkgroup?.workgroup_types) {
        const workgroupIntegrations = currentWorkgroup.workgroup_types.map((type) => ({
          id: type,
          name: type.charAt(0).toUpperCase() + type.slice(1),
        }));
        setIntegrations(workgroupIntegrations);
      } else {
        setIntegrations([]);
      }
      setSelectedIntegration('');
    } else {
      setIntegrations([]);
      setSelectedIntegration('');
    }
  }, [selectedWorkgroup, workgroups]);

  // Load challenges when integration changes
  useEffect(() => {
    if (selectedIntegration) {
      loadFinishedChallenges();
      setCurrentPage(1);
    }
  }, [selectedIntegration]);

  const loadFinishedChallenges = async () => {
    if (!selectedIntegration) return;
    setChallengesLoading(true);
    setSelectedChallenge(null);
    setSelectedChallengeError(null);
    setSelectedChallengeLoading(false);
    latestSelectedChallengeIdRef.current = null;
    try {
      const { items, lastDoc, hasMore: more } = await fetchDisplayChallengesPage(
        selectedIntegration,
        pageSize,
        null
      );
      setPages(items.length ? [items] : []);
      setPageCursors(items.length ? [lastDoc] : []);
      setHasMore(!!more);
      setSelectedChallenge(null);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to load challenges:', err);
      setPages([]);
      setPageCursors([]);
      setHasMore(false);
      setSelectedChallenge(null);
      setSelectedChallengeError(null);
      setSelectedChallengeLoading(false);
      latestSelectedChallengeIdRef.current = null;
    } finally {
      setChallengesLoading(false);
    }
  };

  // Date formatting helper
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter challenges based on search query
  const filteredChallenges = useMemo(() => {
    if (!searchQuery.trim()) {
      return pages.length ? pages[currentPage - 1] : [];
    }
    const query = searchQuery.toLowerCase();
    const currentPageChallenges = pages.length ? pages[currentPage - 1] : [];
    return currentPageChallenges.filter((challenge) => {
      return (
        challenge.id.toLowerCase().includes(query) ||
        challenge.status?.toLowerCase().includes(query) ||
        formatDate(challenge.created_at).toLowerCase().includes(query)
      );
    });
  }, [pages, currentPage, searchQuery]);

  const displayedChallenges = filteredChallenges;
  const totalChallenges = pages.reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < pages.length || hasMore;

  const goPrev = () => {
    if (!canGoPrev) return;
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const goNext = async () => {
    if (!canGoNext) return;
    if (currentPage < pages.length) {
      setCurrentPage((p) => p + 1);
      return;
    }
    try {
      setChallengesLoading(true);
      const lastCursor = pageCursors[pageCursors.length - 1] || null;
      const { items, lastDoc, hasMore: more } = await fetchDisplayChallengesPage(
        selectedIntegration,
        pageSize,
        lastCursor
      );
      if (items.length) {
        setPages((prev) => [...prev, items]);
        setPageCursors((prev) => [...prev, lastDoc]);
        setHasMore(!!more);
        setCurrentPage((p) => p + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load next page:', err);
    } finally {
      setChallengesLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const handleSelectChallenge = useCallback(
    async (challenge: Challenge) => {
      if (!challenge?.id) {
        setSelectedChallenge(null);
        setSelectedChallengeError(null);
        setSelectedChallengeLoading(false);
        latestSelectedChallengeIdRef.current = null;
        return;
      }

      latestSelectedChallengeIdRef.current = challenge.id;
      setSelectedChallenge(challenge);
      setSelectedChallengeError(null);

      if (!selectedIntegration) {
        setSelectedChallengeLoading(false);
        return;
      }

      setSelectedChallengeLoading(true);
      try {
        const detailed = await fetchDisplayChallengeById(selectedIntegration, challenge.id);
        if (latestSelectedChallengeIdRef.current !== challenge.id) {
          return;
        }
        if (detailed) {
          const merged = { ...challenge, ...detailed };
          setSelectedChallenge(merged);
          setPages((prev) =>
            prev.map((page) =>
              page.map((item) => (item.id === challenge.id ? { ...item, ...merged } : item))
            )
          );
        } else {
          setSelectedChallengeError('Challenge details could not be found.');
        }
      } catch (err) {
        console.error('Failed to load challenge details:', err);
        if (latestSelectedChallengeIdRef.current === challenge.id) {
          if (err?.response?.status === 403) {
            setSelectedChallengeError('You need developer permissions to view challenge details.');
          } else {
            setSelectedChallengeError('Failed to load challenge details.');
          }
        }
      } finally {
        if (latestSelectedChallengeIdRef.current === challenge.id) {
          setSelectedChallengeLoading(false);
        }
      }
    },
    [selectedIntegration]
  );

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processSelectedImageFile(file);
    }
  };

  const processSelectedImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }
    setSelectedImage(file);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedIntegration) {
      setError('Please select an integration');
      return;
    }
    if (!selectedImage && (!guidanceText || guidanceText.trim().length === 0)) {
      setError('Please add a screenshot or guidance');
      return;
    }
    if (!selectedWorkgroup) {
      setError('Please select a workgroup');
      return;
    }

    const parsedMaxTurns = maxTurns !== '' ? parseInt(maxTurns, 10) : null;
    if (maxTurns !== '' && (isNaN(parsedMaxTurns!) || parsedMaxTurns! <= 0)) {
      setError('Max turns must be a positive integer');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      await uploadChallenge(
        selectedIntegration,
        selectedImage,
        selectedWorkgroup,
        guidanceText,
        parsedMaxTurns
      );
      setSuccess('Challenge uploaded successfully!');
      setSelectedImage(null);
      setImagePreview(null);
      setGuidanceText('');
      setMaxTurns('');
      setShowUploadForm(false);
      await loadFinishedChallenges();
    } catch (err: any) {
      setError(err.message || 'Failed to upload challenge');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleChallengeRerun = useCallback(
    async (options?: { systemPrompt?: string }) => {
      if (!selectedChallenge) return;

      try {
        const chunks = Array.isArray(selectedChallenge.chunks) ? selectedChallenge.chunks : [];
        if (chunks.length) {
          await deleteChunks(chunks);
        }
      } catch (rerunError) {
        console.warn('ChallengesNew: deleteChunks failed during rerun prep', rerunError);
      }

      try {
        const b64 = selectedChallenge.image_base64;
        if (b64 && typeof b64 === 'string' && b64.length > 0) {
          const byteString = atob(b64);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i += 1) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: 'image/jpeg' });
          const file = new File([blob], `challenge_${selectedChallenge.id}.jpg`, { type: 'image/jpeg' });
          setSelectedImage(file);
          setImagePreview(`data:image/jpeg;base64,${b64}`);
        } else {
          setSelectedImage(null);
          setImagePreview(null);
        }
      } catch (conversionError) {
        setSelectedImage(null);
        setImagePreview(null);
      }

      const overridePrompt = options?.systemPrompt?.trim();
      setGuidanceText(
        overridePrompt && overridePrompt.length
          ? overridePrompt
          : selectedChallenge.guidance || ''
      );

      setMaxTurns(
        selectedChallenge.max_turns != null && !Number.isNaN(Number(selectedChallenge.max_turns))
          ? String(selectedChallenge.max_turns)
          : ''
      );

      if (selectedChallenge.integration_id) {
        setSelectedIntegration(selectedChallenge.integration_id);
      }
      if (selectedChallenge.workgroup_id) {
        setSelectedWorkgroup(selectedChallenge.workgroup_id);
      }

      setShowUploadForm(true);
      setError(null);
      setSuccess(null);
    },
    [selectedChallenge]
  );

  // Master List Rendering
  const renderMasterList = () => {
    if (!selectedIntegration) {
      return (
        <EmptyState
          icon={<Upload className="w-8 h-8 text-muted-foreground" />}
          title="No Integration Selected"
          description="Choose an integration from the filter above"
        />
      );
    }

    if (challengesLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (totalChallenges === 0) {
      return (
        <EmptyState
          icon={<CheckCircle className="w-8 h-8 text-muted-foreground" />}
          title="No Challenges Found"
          description="Create your first challenge to get started"
        />
      );
    }

    return (
      <div>
        {displayedChallenges.map((challenge) => (
          <MasterListItem
            key={challenge.id}
            title={challenge.id}
            subtitle={formatDate(challenge.created_at)}
            icon={
              challenge.image_base64 ? (
                <div className="relative group">
                  <img
                    src={`data:image/jpeg;base64,${challenge.image_base64}`}
                    alt="Thumbnail"
                    className="w-10 h-10 rounded object-cover cursor-pointer"
                  />
                  {/* Hover preview - centered on screen */}
                  <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 pointer-events-none">
                    <img
                      src={`data:image/jpeg;base64,${challenge.image_base64}`}
                      alt="Preview"
                      className="max-w-2xl max-h-[80vh] w-auto h-auto rounded-lg shadow-2xl border-4 border-white"
                    />
                  </div>
                </div>
              ) : (
                <Eye className="w-6 h-6 text-muted-foreground" />
              )
            }
            isSelected={selectedChallenge?.id === challenge.id}
            onClick={() => handleSelectChallenge(challenge)}
            badge={
              challenge.status ? (
                <div className="shrink-0">
                  {getStatusIcon(challenge.status)}
                </div>
              ) : undefined
            }
          />
        ))}

        {/* Pagination */}
        {(canGoPrev || canGoNext) && (
          <div className="flex items-center justify-between p-4 border-t">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={!canGoPrev}>
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage}
              {hasMore || currentPage < pages.length ? '…' : ''}
            </span>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!canGoNext}>
              Next
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Detail Panel Rendering
  const renderDetailPanel = () => {
    if (showUploadForm) {
      return renderUploadForm();
    }

    if (!selectedChallenge) {
      return (
        <EmptyState
          icon={<Eye className="w-12 h-12 text-muted-foreground" />}
          title="Select a Challenge"
          description="Choose a challenge from the list to view its details"
        />
      );
    }

    if (selectedChallengeLoading) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3" style={{ backgroundColor: DETAIL_BACKGROUND }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Loading challenge details…</p>
        </div>
      );
    }

    if (selectedChallengeError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center" style={{ backgroundColor: DETAIL_BACKGROUND }}>
          <AlertCircle className="w-10 h-10 text-red-500" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Unable to load challenge</h3>
            <p className="mt-1 text-sm text-muted-foreground">{selectedChallengeError}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedChallenge) {
                  handleSelectChallenge(selectedChallenge);
                }
              }}
            >
              Retry
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedChallenge(null);
                setSelectedChallengeError(null);
                setSelectedChallengeLoading(false);
                latestSelectedChallengeIdRef.current = null;
              }}
            >
              Close
            </Button>
          </div>
        </div>
      );
    }

    return renderChallengeDetails();
  };

  // Upload Form
  const renderUploadForm = () => {
    const submitDisabled =
      uploading ||
      !selectedIntegration ||
      !selectedWorkgroup ||
      (!selectedImage && (!guidanceText || guidanceText.trim().length === 0));

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
        <DetailPanelHeader
          title="Upload new challenge"
          subtitle="Provision a fresh benchmarking scenario for the selected integration."
          actions={
            <Button variant="outline" size="sm" onClick={() => setShowUploadForm(false)}>
              Cancel
            </Button>
          }
        />

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-4xl space-y-6 px-6 py-8">
            <DetailSection
              icon={<Info className="h-5 w-5" />}
              title="Workgroup & integration"
              subtitle="Choose the workgroup context and target integration."
            >
              <div className="space-y-4">
                <WorkgroupSelect
                  workgroups={workgroups.length ? workgroups : undefined}
                  selectedWorkgroup={selectedWorkgroup}
                  onSelect={setSelectedWorkgroup}
                  label="Workgroup *"
                  autoSelectFirst={false}
                />
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Integration *</label>
                  <select
                    value={selectedIntegration}
                    onChange={(e) => setSelectedIntegration(e.target.value)}
                    className="w-full rounded-md border px-3 py-2 text-sm bg-background"
                  >
                    <option value="">Select an integration</option>
                    {integrations.map((integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name} ({integration.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </DetailSection>

            <DetailSection
              icon={<Image className="h-5 w-5" />}
              title="Screenshot"
              subtitle="Attach an optional screenshot to give reviewers visual context."
            >
              <div className="flex flex-col gap-4">
                <ImageUploadDropzone
                  onChange={handleImageSelect}
                  helperText="PNG, JPG up to 5MB"
                  secondaryText="or paste"
                  className="bg-muted/30 hover:bg-muted/50"
                />

                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-w-full max-h-48 rounded-lg border"
                    />
                    <button
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute -top-2 -right-2 rounded-full bg-destructive p-1 text-destructive-foreground shadow hover:bg-destructive/90"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </DetailSection>

            <DetailSection
              icon={<FileCode className="h-5 w-5" />}
              title="Guidance & limits"
              subtitle="Suggest how the agent should tackle the problem and optionally cap turn count."
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Guidance (optional)
                  </label>
                  <Textarea
                    value={guidanceText}
                    onChange={(e) => setGuidanceText(e.target.value)}
                    placeholder="Optionally steer the AI toward a solution..."
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Max turns (optional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={maxTurns}
                    onChange={(e) => setMaxTurns(e.target.value)}
                    placeholder="Leave blank for default"
                    className="w-full rounded-md border px-3 py-2 text-sm bg-background"
                  />
                </div>
              </div>
            </DetailSection>

            <DetailSection
              icon={<Layers className="h-5 w-5" />}
              title="Submit challenge"
              subtitle="Upload immediately or rerun after adjusting screenshot/guidance."
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  Provide either a screenshot or guidance before uploading.
                </p>
                <Button
                  onClick={handleUpload}
                  disabled={submitDisabled}
                  className="flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Challenge
                    </>
                  )}
                </Button>
              </div>
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  // Challenge Details
  const renderChallengeDetails = () => {
    if (!selectedChallenge) return null;

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
        <ChallengeDetailsPanel
          challenge={selectedChallenge}
          onClose={() => {
            setSelectedChallenge(null);
            setSelectedChallengeError(null);
            setSelectedChallengeLoading(false);
            latestSelectedChallengeIdRef.current = null;
          }}
          onRerun={handleChallengeRerun}
        />
      </div>
    );
  };

  const renderHeaderControls = () => {
    return (
      <div className="space-y-3">
        <WorkgroupSelect
          selectedWorkgroup={selectedWorkgroup}
          onSelect={setSelectedWorkgroup}
          autoSelectFirst={false}
          onWorkgroupsLoaded={handleWorkgroupsLoaded}
        />
        <div>
          <label className="block text-xs font-medium mb-1">Integration</label>
          <select
            value={selectedIntegration}
            onChange={(e) => setSelectedIntegration(e.target.value)}
            className="w-full px-3 py-2 text-sm border rounded-md bg-background"
          >
            <option value="">Select integration</option>
            {integrations.map((integration) => (
              <option key={integration.id} value={integration.id}>
                {integration.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-full h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
      {/* Error/Success Messages */}
      {(error || success) && (
        <div className="border-b bg-card px-6 py-3">
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">×</button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
            </div>
          )}
        </div>
      )}

      {/* Master-Detail Layout */}
      <MasterDetailLayout
        masterTitle="Challenges"
        masterItems={renderMasterList()}
        detailContent={renderDetailPanel()}
        onNewItem={() => setShowUploadForm(true)}
        newItemLabel="New Challenge"
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by ID, status, or date..."
        showSearch
        headerControls={renderHeaderControls()}
        selectedBackground={DETAIL_BACKGROUND}
      />

    </div>
  );
};

export default ChallengesNew;
